import { PlusOutlined } from '@ant-design/icons';
import {
  Button,
  Descriptions,
  Drawer,
  Empty,
  Form,
  Input,
  Modal,
  message,
  Popconfirm,
  Select,
  Space,
  Tag,
  Typography,
  Upload,
} from 'antd';
import React from 'react';
import Table from '@/components/ThemedTable';
import { parkApi } from '@/services/bff/park.api';
import type {
  EnterpriseArchiveAttachment,
  EnterpriseArchiveListQuery,
  EnterpriseContact,
  EnterpriseContactRequest,
  EnterpriseIntellectualProperty,
  EnterpriseIntellectualPropertyRequest,
  EnterpriseProduct,
  EnterpriseProductRequest,
  EnterpriseQualification,
  EnterpriseQualificationRequest,
} from '@/services/bff/types/park';
import {
  businessTableColumns,
  businessTableScroll,
} from '@/utils/businessTableColumns';
import { formatDate, formatDateTime } from '@/utils/datetime';

type ArchiveItem =
  | EnterpriseContact
  | EnterpriseProduct
  | EnterpriseQualification
  | EnterpriseIntellectualProperty;
type ArchiveKind =
  | 'contacts'
  | 'products'
  | 'qualifications'
  | 'intellectual-properties';
type ArchiveFormValues = Record<string, unknown>;

const typeOptions: Record<
  ArchiveKind,
  Array<{ value: string; label: string }>
> = {
  contacts: [
    { value: 'enterprise_principal', label: '企业负责人' },
    { value: 'park', label: '园区联系人' },
    { value: 'finance', label: '财务联系人' },
    { value: 'service', label: '服务联系人' },
    { value: 'financing', label: '融资联系人' },
    { value: 'legal', label: '法务联系人' },
    { value: 'other', label: '其他' },
  ],
  products: [
    { value: 'product', label: '产品' },
    { value: 'technology', label: '技术能力' },
    { value: 'solution', label: '解决方案' },
  ],
  qualifications: [
    { value: 'high_tech', label: '高新技术企业' },
    { value: 'specialized', label: '专精特新' },
    { value: 'iso', label: 'ISO' },
    { value: 'license', label: '行业许可证' },
    { value: 'other', label: '其他' },
  ],
  'intellectual-properties': [
    { value: 'invention_patent', label: '发明专利' },
    { value: 'utility_patent', label: '实用新型' },
    { value: 'design_patent', label: '外观专利' },
    { value: 'trademark', label: '商标' },
    { value: 'software_copyright', label: '软件著作权' },
    { value: 'other', label: '其他' },
  ],
};

const statusOptions: Record<
  'qualifications' | 'intellectual-properties',
  Array<{ value: string; label: string }>
> = {
  qualifications: [
    { value: 'valid', label: '有效' },
    { value: 'expiring', label: '即将到期' },
    { value: 'expired', label: '已过期' },
    { value: 'pending_review', label: '待复审' },
    { value: 'applying', label: '申报中' },
    { value: 'invalid', label: '失效' },
  ],
  'intellectual-properties': [
    { value: 'valid', label: '有效' },
    { value: 'expired', label: '已过期' },
    { value: 'pending_renewal', label: '待续展' },
    { value: 'invalid', label: '失效' },
  ],
};

const stageOptions = [
  { value: 'idea', label: '构想期' },
  { value: '研发中', label: '研发中' },
  { value: 'pilot', label: '试点期' },
  { value: 'commercial', label: '商业化' },
  { value: 'scaling', label: '规模化' },
];

const labelOf = (
  options: Array<{ value: string; label: string }>,
  value?: string,
) => options.find((item) => item.value === value)?.label || value || '-';

const archiveName = (item: ArchiveItem) => item.name || '-';
const isContact = (item: ArchiveItem): item is EnterpriseContact =>
  'position' in item && 'phone' in item && 'isPrimary' in item;
const isProduct = (item: ArchiveItem): item is EnterpriseProduct =>
  'currentStage' in item ||
  'coreTechnology' in item ||
  'applicationAreas' in item;
const isQualification = (item: ArchiveItem): item is EnterpriseQualification =>
  'certificateNumber' in item || 'validUntil' in item;
const isIntellectualProperty = (
  item: ArchiveItem,
): item is EnterpriseIntellectualProperty =>
  'applicationNumber' in item ||
  'authorizationNumber' in item ||
  'rightHolder' in item;
const archiveType = (_kind: ArchiveKind, item: ArchiveItem) => {
  return 'type' in item ? item.type : undefined;
};
const archiveStatus = (item: ArchiveItem) =>
  isQualification(item) || isIntellectualProperty(item)
    ? item.status
    : undefined;

const attachmentsOf = (
  item:
    | EnterpriseProduct
    | EnterpriseQualification
    | EnterpriseIntellectualProperty,
) => {
  return (item.fileUuids || []).map((fileUuid) => ({
    uuid: fileUuid,
    fileUuid,
  }));
};

const ArchiveAttachmentField: React.FC<{
  value?: string[];
  onChange?: (fileUuids: string[]) => void;
}> = ({ value = [], onChange }) => (
  <Space direction="vertical" style={{ width: '100%' }}>
    <Upload
      showUploadList={false}
      beforeUpload={async (file) => {
        try {
          const uploaded = await parkApi.uploadFile(
            file as File,
            'enterprise_profile',
          );
          onChange?.([...value, uploaded.uuid]);
          message.success('附件已上传');
        } catch {
          message.error('附件上传失败');
        }
        return false;
      }}
    >
      <Button>上传附件</Button>
    </Upload>
    {value.map((fileUuid) => (
      <Space key={fileUuid}>
        <Typography.Text>{fileUuid}</Typography.Text>
        <a onClick={() => void openAttachment({ uuid: fileUuid, fileUuid })}>
          查看
        </a>
        <a
          onClick={() =>
            void openAttachment({ uuid: fileUuid, fileUuid }, true)
          }
        >
          下载
        </a>
        <a
          onClick={() => onChange?.(value.filter((item) => item !== fileUuid))}
        >
          移除
        </a>
      </Space>
    ))}
  </Space>
);

const openAttachment = async (
  attachment: EnterpriseArchiveAttachment,
  download = false,
) => {
  try {
    let url = attachment.url || attachment.fileUrl;
    if (!url && attachment.fileUuid)
      url = (await parkApi.getFile(attachment.fileUuid)).url;
    if (!url) {
      message.error('附件地址不存在');
      return;
    }
    if (download) {
      const link = document.createElement('a');
      link.href = url;
      link.download = attachment.fileName || '企业档案附件';
      link.target = '_blank';
      link.rel = 'noreferrer';
      document.body.appendChild(link);
      link.click();
      link.remove();
      return;
    }
    window.open(url, '_blank', 'noopener,noreferrer');
  } catch {
    message.error('附件打开失败');
  }
};

const listArchive = async (
  kind: ArchiveKind,
  enterpriseTenantUuid: string | undefined,
  params: EnterpriseArchiveListQuery,
  parkEnterpriseUuid?: string,
) => {
  if (kind === 'contacts')
    return parkApi.listEnterpriseContacts(
      enterpriseTenantUuid as string,
      params,
    );
  if (kind === 'products')
    return parkApi.listEnterpriseProducts(
      enterpriseTenantUuid as string,
      params,
    );
  if (kind === 'qualifications')
    return parkEnterpriseUuid
      ? parkApi.listLocalEnterpriseQualifications(parkEnterpriseUuid, params)
      : parkApi.listEnterpriseQualifications(
          enterpriseTenantUuid as string,
          params,
        );
  return parkEnterpriseUuid
    ? parkApi.listLocalEnterpriseIntellectualProperties(
        parkEnterpriseUuid,
        params,
      )
    : parkApi.listEnterpriseIntellectualProperties(
        enterpriseTenantUuid as string,
        params,
      );
};

const getArchive = async (
  kind: ArchiveKind,
  enterpriseTenantUuid: string | undefined,
  uuid: string,
  parkEnterpriseUuid?: string,
) => {
  if (kind === 'contacts')
    return parkApi.getEnterpriseContact(enterpriseTenantUuid as string, uuid);
  if (kind === 'products')
    return parkApi.getEnterpriseProduct(enterpriseTenantUuid as string, uuid);
  if (kind === 'qualifications')
    return parkEnterpriseUuid
      ? parkApi.getLocalEnterpriseQualification(parkEnterpriseUuid, uuid)
      : parkApi.getEnterpriseQualification(
          enterpriseTenantUuid as string,
          uuid,
        );
  return parkEnterpriseUuid
    ? parkApi.getLocalEnterpriseIntellectualProperty(parkEnterpriseUuid, uuid)
    : parkApi.getEnterpriseIntellectualProperty(
        enterpriseTenantUuid as string,
        uuid,
      );
};

const saveArchive = async (
  kind: ArchiveKind,
  enterpriseTenantUuid: string | undefined,
  editing: ArchiveItem | undefined,
  values: ArchiveFormValues,
  parkEnterpriseUuid?: string,
) => {
  const text = (key: string) =>
    typeof values[key] === 'string' ? (values[key] as string) : undefined;
  const files = Array.isArray(values.fileUuids)
    ? values.fileUuids.filter(
        (value): value is string => typeof value === 'string',
      )
    : undefined;
  if (kind === 'contacts') {
    const data: EnterpriseContactRequest = {
      name: text('name') || '',
      position: text('position') || '',
      phone: text('phone') || '',
      email: text('email'),
      type: text('type') || '',
      isPrimary: values.isPrimary === true,
      remark: text('remark'),
    };
    return editing
      ? parkApi.updateEnterpriseContact(
          enterpriseTenantUuid as string,
          editing.uuid,
          data,
        )
      : parkApi.createEnterpriseContact(enterpriseTenantUuid as string, data);
  }
  if (kind === 'products') {
    const data: EnterpriseProductRequest = {
      name: text('name') || '',
      type: text('type') || '',
      introduction: text('introduction'),
      coreTechnology: text('coreTechnology'),
      applicationAreas: text('applicationAreas'),
      currentStage: text('currentStage'),
      remark: text('remark'),
      fileUuids: files,
    };
    return editing
      ? parkApi.updateEnterpriseProduct(
          enterpriseTenantUuid as string,
          editing.uuid,
          data,
        )
      : parkApi.createEnterpriseProduct(enterpriseTenantUuid as string, data);
  }
  if (kind === 'qualifications') {
    const data: EnterpriseQualificationRequest = {
      name: text('name') || '',
      type: text('type') || '',
      certificateNumber: text('certificateNumber'),
      issuingAuthority: text('issuingAuthority'),
      issueDate: text('issueDate'),
      validUntil: text('validUntil'),
      status: text('status') || '',
      fileUuids: files,
    };
    return editing
      ? parkEnterpriseUuid
        ? parkApi.updateLocalEnterpriseQualification(
            parkEnterpriseUuid,
            editing.uuid,
            data,
          )
        : parkApi.updateEnterpriseQualification(
            enterpriseTenantUuid as string,
            editing.uuid,
            data,
          )
      : parkEnterpriseUuid
        ? parkApi.createLocalEnterpriseQualification(parkEnterpriseUuid, data)
        : parkApi.createEnterpriseQualification(
            enterpriseTenantUuid as string,
            data,
          );
  }
  const data: EnterpriseIntellectualPropertyRequest = {
    name: text('name') || '',
    type: text('type') || '',
    applicationNumber: text('applicationNumber'),
    authorizationNumber: text('authorizationNumber'),
    applicationDate: text('applicationDate'),
    authorizationDate: text('authorizationDate'),
    status: text('status') || '',
    rightHolder: text('rightHolder'),
    fileUuids: files,
  };
  return editing
    ? parkEnterpriseUuid
      ? parkApi.updateLocalEnterpriseIntellectualProperty(
          parkEnterpriseUuid,
          editing.uuid,
          data,
        )
      : parkApi.updateEnterpriseIntellectualProperty(
          enterpriseTenantUuid as string,
          editing.uuid,
          data,
        )
    : parkEnterpriseUuid
      ? parkApi.createLocalEnterpriseIntellectualProperty(
          parkEnterpriseUuid,
          data,
        )
      : parkApi.createEnterpriseIntellectualProperty(
          enterpriseTenantUuid as string,
          data,
        );
};

const deleteArchive = (
  kind: ArchiveKind,
  enterpriseTenantUuid: string | undefined,
  uuid: string,
  parkEnterpriseUuid?: string,
) => {
  if (kind === 'contacts')
    return parkApi.deleteEnterpriseContact(
      enterpriseTenantUuid as string,
      uuid,
    );
  if (kind === 'products')
    return parkApi.deleteEnterpriseProduct(
      enterpriseTenantUuid as string,
      uuid,
    );
  if (kind === 'qualifications')
    return parkEnterpriseUuid
      ? parkApi.deleteLocalEnterpriseQualification(parkEnterpriseUuid, uuid)
      : parkApi.deleteEnterpriseQualification(
          enterpriseTenantUuid as string,
          uuid,
        );
  return parkEnterpriseUuid
    ? parkApi.deleteLocalEnterpriseIntellectualProperty(
        parkEnterpriseUuid,
        uuid,
      )
    : parkApi.deleteEnterpriseIntellectualProperty(
        enterpriseTenantUuid as string,
        uuid,
      );
};

const detailItems = (kind: ArchiveKind, archiveItem: ArchiveItem) => {
  if (kind === 'contacts' && isContact(archiveItem)) {
    const item = archiveItem;
    return [
      { key: 'name', label: '姓名', children: item.name },
      {
        key: 'type',
        label: '联系人类型',
        children: labelOf(typeOptions.contacts, item.type),
      },
      { key: 'position', label: '职务', children: item.position || '-' },
      { key: 'phone', label: '手机', children: item.phone || '-' },
      { key: 'email', label: '邮箱', children: item.email || '-' },
      {
        key: 'isPrimary',
        label: '主要联系人',
        children: item.isPrimary ? '是' : '否',
      },
      {
        key: 'updatedAt',
        label: '更新时间',
        children: formatDateTime(item.updatedAt),
      },
    ];
  }
  if (kind === 'products' && isProduct(archiveItem)) {
    const item = archiveItem;
    return [
      { key: 'name', label: '名称', children: item.name },
      {
        key: 'type',
        label: '产品类型',
        children: labelOf(typeOptions.products, item.type),
      },
      {
        key: 'stage',
        label: '产品阶段',
        children: labelOf(stageOptions, item.currentStage),
      },
      {
        key: 'applicationAreas',
        label: '应用领域',
        children: item.applicationAreas || '-',
      },
      {
        key: 'updatedAt',
        label: '更新时间',
        children: formatDateTime(item.updatedAt),
      },
    ];
  }
  if (kind === 'qualifications' && isQualification(archiveItem)) {
    const item = archiveItem;
    return [
      { key: 'name', label: '资质名称', children: item.name },
      {
        key: 'type',
        label: '资质类型',
        children: labelOf(typeOptions.qualifications, item.type),
      },
      {
        key: 'certificateNumber',
        label: '证书编号',
        children: item.certificateNumber || '-',
      },
      {
        key: 'issuingAuthority',
        label: '发证机构',
        children: item.issuingAuthority || '-',
      },
      {
        key: 'issueDate',
        label: '认定日期',
        children: formatDate(item.issueDate),
      },
      {
        key: 'validUntil',
        label: '到期日期',
        children: formatDate(item.validUntil),
      },
      {
        key: 'status',
        label: '状态',
        children: labelOf(statusOptions.qualifications, item.status),
      },
      {
        key: 'updatedAt',
        label: '更新时间',
        children: formatDateTime(item.updatedAt),
      },
    ];
  }
  if (
    kind === 'intellectual-properties' &&
    isIntellectualProperty(archiveItem)
  ) {
    const item = archiveItem;
    return [
      { key: 'name', label: '名称', children: item.name },
      {
        key: 'type',
        label: '类型',
        children: labelOf(typeOptions['intellectual-properties'], item.type),
      },
      {
        key: 'applicationNumber',
        label: '申请/注册号',
        children: item.applicationNumber || '-',
      },
      {
        key: 'applicationDate',
        label: '申请日期',
        children: formatDate(item.applicationDate),
      },
      {
        key: 'authorizationDate',
        label: '授权日期',
        children: formatDate(item.authorizationDate),
      },
      {
        key: 'rightHolder',
        label: '权利人',
        children: item.rightHolder || '-',
      },
      {
        key: 'status',
        label: '状态',
        children: labelOf(
          statusOptions['intellectual-properties'],
          item.status,
        ),
      },
      {
        key: 'updatedAt',
        label: '更新时间',
        children: formatDateTime(item.updatedAt),
      },
    ];
  }
  return [];
};

const formInitialValues = (kind: ArchiveKind, item?: ArchiveItem) => {
  if (!item)
    return kind === 'contacts'
      ? { type: 'enterprise_principal', isPrimary: false }
      : kind === 'products'
        ? { type: 'product' }
        : { status: 'valid' };
  return { ...item };
};

export const ArchivePanel: React.FC<{
  enterpriseTenantUuid?: string;
  parkEnterpriseUuid?: string;
  kind: ArchiveKind;
  readOnly: boolean;
  fitColumns?: boolean;
}> = ({
  enterpriseTenantUuid,
  parkEnterpriseUuid,
  kind,
  readOnly,
  fitColumns = false,
}) => {
  const [items, setItems] = React.useState<ArchiveItem[]>([]);
  const [total, setTotal] = React.useState(0);
  const [page, setPage] = React.useState(1);
  const [loading, setLoading] = React.useState(false);
  const [loadError, setLoadError] = React.useState(false);
  const [draftKeyword, setDraftKeyword] = React.useState('');
  const [draftType, setDraftType] = React.useState<string>();
  const [draftStatus, setDraftStatus] = React.useState<string>();
  const [query, setQuery] = React.useState<EnterpriseArchiveListQuery>({
    pageNumber: 1,
    pageSize: 20,
  });
  const [editing, setEditing] = React.useState<ArchiveItem>();
  const [editorOpen, setEditorOpen] = React.useState(false);
  const [detail, setDetail] = React.useState<ArchiveItem>();
  const [form] = Form.useForm();

  const title =
    kind === 'contacts'
      ? '团队联系人'
      : kind === 'products'
        ? '产品与技术'
        : kind === 'qualifications'
          ? '企业资质'
          : '知识产权';
  const emptyDescription =
    parkEnterpriseUuid &&
    (kind === 'qualifications' || kind === 'intellectual-properties')
      ? total > 0 || query.keyword || query.type || query.status
        ? `未找到符合条件的${title}`
        : `尚未录入${title}，请点击上方“新增${title}”添加`
      : `暂无${title}`;
  const load = React.useCallback(async () => {
    setLoading(true);
    setLoadError(false);
    try {
      const result = await listArchive(
        kind,
        enterpriseTenantUuid,
        query,
        parkEnterpriseUuid,
      );
      setItems(result.items);
      setTotal(result.total);
    } catch {
      setItems([]);
      setTotal(0);
      setLoadError(true);
      message.error(`${title}加载失败`);
    } finally {
      setLoading(false);
    }
  }, [enterpriseTenantUuid, parkEnterpriseUuid, kind, query, title]);

  React.useEffect(() => {
    void load();
  }, [load]);

  const openEditor = (item?: ArchiveItem) => {
    setEditing(item);
    form.setFieldsValue(formInitialValues(kind, item));
    setEditorOpen(true);
  };

  const openDetail = async (item: ArchiveItem) => {
    try {
      setDetail(
        await getArchive(
          kind,
          enterpriseTenantUuid,
          item.uuid,
          parkEnterpriseUuid,
        ),
      );
    } catch {
      message.error(`${title}详情加载失败`);
    }
  };

  const handleDelete = async (item: ArchiveItem) => {
    try {
      await deleteArchive(
        kind,
        enterpriseTenantUuid,
        item.uuid,
        parkEnterpriseUuid,
      );
      message.success('已删除');
      await load();
    } catch {
      message.error('删除失败');
    }
  };

  const applyFilters = () => {
    const nextQuery = {
      pageNumber: 1,
      pageSize: 20,
      keyword: draftKeyword || undefined,
      type: draftType,
      status: draftStatus,
    };
    setPage(1);
    setQuery(nextQuery);
  };

  const changePage = (nextPage: number) => {
    setPage(nextPage);
    setQuery((current) => ({ ...current, pageNumber: nextPage }));
  };

  const attachments =
    detail &&
    (isProduct(detail) ||
      isQualification(detail) ||
      isIntellectualProperty(detail))
      ? attachmentsOf(detail)
      : [];

  const columns = [
    {
      title: kind === 'contacts' ? '姓名' : '名称',
      render: (_: unknown, item: ArchiveItem) => (
        <a title={archiveName(item)} onClick={() => void openDetail(item)}>
          {archiveName(item)}
        </a>
      ),
    },
    {
      title: '类型',
      render: (_: unknown, item: ArchiveItem) =>
        labelOf(typeOptions[kind], archiveType(kind, item)),
    },
    ...(kind === 'contacts'
      ? [
          {
            title: '职务',
            render: (_: unknown, item: ArchiveItem) =>
              'position' in item ? item.position || '-' : '-',
          },
        ]
      : []),
    ...(kind === 'products'
      ? [
          {
            title: '阶段',
            render: (_: unknown, item: ArchiveItem) =>
              labelOf(
                stageOptions,
                isProduct(item) ? item.currentStage : undefined,
              ),
          },
        ]
      : []),
    ...(kind === 'qualifications'
      ? [
          {
            title: '证书编号',
            render: (_: unknown, item: ArchiveItem) =>
              isQualification(item) ? item.certificateNumber || '-' : '-',
          },
          {
            title: '有效期',
            render: (_: unknown, item: ArchiveItem) =>
              formatDate(isQualification(item) ? item.validUntil : undefined),
          },
        ]
      : []),
    ...(kind === 'intellectual-properties'
      ? [
          {
            title: '申请/注册号',
            render: (_: unknown, item: ArchiveItem) =>
              isIntellectualProperty(item)
                ? item.applicationNumber || '-'
                : '-',
          },
          {
            title: '授权日期',
            render: (_: unknown, item: ArchiveItem) =>
              formatDate(
                isIntellectualProperty(item)
                  ? item.authorizationDate
                  : undefined,
              ),
          },
        ]
      : []),
    ...(kind === 'qualifications' || kind === 'intellectual-properties'
      ? [
          {
            title: '状态',
            render: (_: unknown, item: ArchiveItem) => (
              <Tag>{labelOf(statusOptions[kind], archiveStatus(item))}</Tag>
            ),
          },
        ]
      : []),
    {
      title: '更新时间',
      render: (_: unknown, item: ArchiveItem) => formatDateTime(item.updatedAt),
    },
    ...(kind === 'products' ||
    kind === 'qualifications' ||
    kind === 'intellectual-properties'
      ? [
          {
            title: '附件',
            render: (_: unknown, item: ArchiveItem) =>
              (isProduct(item) ||
                isQualification(item) ||
                isIntellectualProperty(item)) &&
              attachmentsOf(item).length
                ? '有'
                : '无',
          },
        ]
      : []),
    {
      title: '操作',
      dataIndex: 'option',
      fixed: 'right' as const,
      render: (_: unknown, item: ArchiveItem) => (
        <Space size={16}>
          <a key="detail" onClick={() => void openDetail(item)}>
            详情
          </a>
          {!readOnly ? (
            <a key="edit" onClick={() => openEditor(item)}>
              编辑
            </a>
          ) : null}
          {!readOnly ? (
            <Popconfirm
              key="delete"
              title="确认删除该记录？"
              onConfirm={() => void handleDelete(item)}
            >
              <a>删除</a>
            </Popconfirm>
          ) : null}
        </Space>
      ),
    },
  ];

  const readableColumns = fitColumns
    ? businessTableColumns(
        columns.map((column) => ({
          ...column,
          fixed: undefined,
          width: (
            {
              类型: 140,
              职务: 140,
              阶段: 120,
              证书编号: 190,
              有效期: 130,
              '申请/注册号': 190,
              授权日期: 130,
              状态: 110,
              更新时间: 175,
              附件: 80,
              操作: 180,
            } as Record<string, number>
          )[column.title],
          ellipsis: [
            '名称',
            '姓名',
            '职务',
            '证书编号',
            '申请/注册号',
          ].includes(column.title),
        })),
      )
    : columns;

  return (
    <Space direction="vertical" style={{ width: '100%' }} size="middle">
      <Space wrap>
        <Input
          placeholder="关键词"
          value={draftKeyword}
          onChange={(event) => setDraftKeyword(event.target.value)}
        />
        <Select
          allowClear
          placeholder="类型"
          value={draftType}
          options={typeOptions[kind]}
          onChange={setDraftType}
          style={{ width: 160 }}
        />
        {kind === 'qualifications' || kind === 'intellectual-properties' ? (
          <Select
            allowClear
            placeholder="状态"
            value={draftStatus}
            options={statusOptions[kind]}
            onChange={setDraftStatus}
            style={{ width: 160 }}
          />
        ) : null}
        <Button onClick={applyFilters}>查询</Button>
        {!readOnly ? (
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => openEditor()}
          >
            新增{title.replace('团队', '')}
          </Button>
        ) : null}
      </Space>
      {loadError ? (
        <Empty
          description={
            <Space direction="vertical">
              <span>{title}暂时无法加载</span>
              <Button onClick={() => void load()}>重新加载</Button>
            </Space>
          }
        />
      ) : (
        <Table
          rowKey="uuid"
          loading={loading}
          dataSource={items}
          columns={readableColumns}
          tableLayout={fitColumns ? 'fixed' : undefined}
          scroll={
            fitColumns ? { x: businessTableScroll(readableColumns) } : undefined
          }
          locale={{ emptyText: <Empty description={emptyDescription} /> }}
          pagination={{
            current: page,
            pageSize: 20,
            total,
            showSizeChanger: false,
            showTotal: (count: number) => `共 ${count} 条`,
            onChange: changePage,
          }}
        />
      )}

      <Modal
        rootClassName="platform-ui"
        key={editing?.uuid || `${kind}-new`}
        open={editorOpen}
        title={editing ? `编辑${title}` : `新增${title}`}
        onCancel={() => setEditorOpen(false)}
        onOk={() => form.submit()}
        destroyOnHidden
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={async (values) => {
            try {
              await saveArchive(
                kind,
                enterpriseTenantUuid,
                editing,
                values,
                parkEnterpriseUuid,
              );
              message.success(editing ? '已保存' : '已新增');
              setEditorOpen(false);
              await load();
            } catch {
              message.error(editing ? '保存失败' : '新增失败');
            }
          }}
        >
          {kind === 'contacts' ? (
            <>
              <Form.Item
                name="name"
                label="姓名"
                rules={[{ required: true, message: '请输入姓名' }]}
              >
                <Input />
              </Form.Item>
              <Form.Item
                name="type"
                label="联系人类型"
                rules={[{ required: true, message: '请选择联系人类型' }]}
              >
                <Select options={typeOptions.contacts} />
              </Form.Item>
              <Form.Item
                name="position"
                label="职务"
                rules={[{ required: true, message: '请输入职务' }]}
              >
                <Input />
              </Form.Item>
              <Form.Item
                name="phone"
                label="手机"
                rules={[{ required: true, message: '请输入手机' }]}
              >
                <Input />
              </Form.Item>
              <Form.Item name="email" label="邮箱">
                <Input />
              </Form.Item>
              <Form.Item name="isPrimary" label="是否主要联系人">
                <Select
                  options={[
                    { value: true, label: '是' },
                    { value: false, label: '否' },
                  ]}
                />
              </Form.Item>
              <Form.Item name="remark" label="备注">
                <Input.TextArea />
              </Form.Item>
            </>
          ) : null}
          {kind === 'products' ? (
            <>
              <Form.Item
                name="name"
                label="产品/技术名称"
                rules={[{ required: true, message: '请输入名称' }]}
              >
                <Input />
              </Form.Item>
              <Form.Item
                name="type"
                label="类型"
                rules={[{ required: true, message: '请选择类型' }]}
              >
                <Select options={typeOptions.products} />
              </Form.Item>
              <Form.Item name="currentStage" label="阶段">
                <Select options={stageOptions} />
              </Form.Item>
              <Form.Item name="introduction" label="简介">
                <Input.TextArea rows={3} />
              </Form.Item>
              <Form.Item name="coreTechnology" label="核心技术">
                <Input.TextArea rows={3} />
              </Form.Item>
              <Form.Item name="applicationAreas" label="应用领域">
                <Input />
              </Form.Item>
              <Form.Item name="remark" label="备注">
                <Input.TextArea />
              </Form.Item>
              <Form.Item name="fileUuids" label="附件">
                <ArchiveAttachmentField />
              </Form.Item>
            </>
          ) : null}
          {kind === 'qualifications' ? (
            <>
              <Form.Item
                name="name"
                label="资质名称"
                rules={[{ required: true, message: '请输入资质名称' }]}
              >
                <Input />
              </Form.Item>
              <Form.Item
                name="type"
                label="资质类型"
                rules={[{ required: true, message: '请选择资质类型' }]}
              >
                <Select options={typeOptions.qualifications} />
              </Form.Item>
              <Form.Item name="certificateNumber" label="证书编号">
                <Input />
              </Form.Item>
              <Form.Item name="issuingAuthority" label="发证机构">
                <Input />
              </Form.Item>
              <Form.Item name="issueDate" label="认定日期">
                <Input placeholder="YYYY-MM-DD" />
              </Form.Item>
              <Form.Item name="validUntil" label="到期日期">
                <Input placeholder="YYYY-MM-DD" />
              </Form.Item>
              <Form.Item
                name="status"
                label="状态"
                rules={[{ required: true, message: '请选择状态' }]}
              >
                <Select options={statusOptions.qualifications} />
              </Form.Item>
              <Form.Item name="fileUuids" label="附件">
                <ArchiveAttachmentField />
              </Form.Item>
            </>
          ) : null}
          {kind === 'intellectual-properties' ? (
            <>
              <Form.Item
                name="name"
                label="名称"
                rules={[{ required: true, message: '请输入名称' }]}
              >
                <Input />
              </Form.Item>
              <Form.Item
                name="type"
                label="类型"
                rules={[{ required: true, message: '请选择类型' }]}
              >
                <Select options={typeOptions['intellectual-properties']} />
              </Form.Item>
              <Form.Item name="applicationNumber" label="申请/注册号">
                <Input />
              </Form.Item>
              <Form.Item name="authorizationNumber" label="授权号">
                <Input />
              </Form.Item>
              <Form.Item name="applicationDate" label="申请日期">
                <Input placeholder="YYYY-MM-DD" />
              </Form.Item>
              <Form.Item name="authorizationDate" label="授权日期">
                <Input placeholder="YYYY-MM-DD" />
              </Form.Item>
              <Form.Item name="rightHolder" label="权利人">
                <Input />
              </Form.Item>
              <Form.Item
                name="status"
                label="状态"
                rules={[{ required: true, message: '请选择状态' }]}
              >
                <Select options={statusOptions['intellectual-properties']} />
              </Form.Item>
              <Form.Item name="fileUuids" label="附件">
                <ArchiveAttachmentField />
              </Form.Item>
            </>
          ) : null}
        </Form>
      </Modal>

      <Drawer
        rootClassName="platform-ui"
        title={`${title}详情`}
        width={720}
        open={Boolean(detail)}
        onClose={() => setDetail(undefined)}
      >
        {detail ? (
          <Space direction="vertical" size="large" style={{ width: '100%' }}>
            <Descriptions column={2} items={detailItems(kind, detail)} />
            {kind === 'products' && 'introduction' in detail ? (
              <>
                <Typography.Title level={5}>简介</Typography.Title>
                <Typography.Paragraph style={{ whiteSpace: 'pre-wrap' }}>
                  {detail.introduction || '-'}
                </Typography.Paragraph>
                <Typography.Title level={5}>主要功能</Typography.Title>
                <Typography.Paragraph style={{ whiteSpace: 'pre-wrap' }}>
                  {detail.coreTechnology || '-'}
                </Typography.Paragraph>
              </>
            ) : null}
            {kind !== 'contacts' ? (
              <>
                <Typography.Title level={5}>附件</Typography.Title>
                {attachments.length ? (
                  attachments.map((attachment) => (
                    <Space key={attachment.uuid}>
                      <Typography.Text>
                        {attachment.fileUuid || '档案附件'}
                      </Typography.Text>
                      <a onClick={() => void openAttachment(attachment)}>
                        查看
                      </a>
                      <a onClick={() => void openAttachment(attachment, true)}>
                        下载
                      </a>
                    </Space>
                  ))
                ) : (
                  <Empty description="暂无附件" />
                )}
              </>
            ) : null}
          </Space>
        ) : null}
      </Drawer>
    </Space>
  );
};
